import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import orangeLogo from '../assets/orangelog.png';

const SCHEMES = [
  {
    name: 'PubSilo Red',
    label: 'Current',
    logoCol: '#F41238',
    nav: '#13273F',
    strip: '#0d1f33',
    accent: '#dd3333',
    subtitle: '#7a9cbf',
    tagline: 'The flagship — bold red authority with deep navy confidence.',
  },
  {
    name: 'Obsidian',
    label: 'Classic',
    logoCol: '#111827',
    nav: '#1f2937',
    strip: '#111827',
    accent: '#ef4444',
    subtitle: '#6b7280',
    tagline: 'Timeless newspaper black — understated prestige, zero distraction.',
  },
  {
    name: 'Deep Ocean',
    label: 'Editorial',
    logoCol: '#0369a1',
    nav: '#0c2a3f',
    strip: '#061b29',
    accent: '#38bdf8',
    subtitle: '#4a8aaf',
    tagline: 'Cold ocean depths — authoritative and editorially serious.',
  },
  {
    name: 'Verdant',
    label: 'Independent',
    logoCol: '#15803d',
    nav: '#052e16',
    strip: '#022c12',
    accent: '#4ade80',
    subtitle: '#4a8a6a',
    tagline: 'Forest green — community-rooted, independent, and grounded.',
  },
  {
    name: 'Midnight',
    label: 'Premium',
    logoCol: '#6d28d9',
    nav: '#1e1b4b',
    strip: '#0f0a2b',
    accent: '#a78bfa',
    subtitle: '#6a5aaa',
    tagline: 'Deep purple — premium positioning and distinguished tone.',
  },
  {
    name: 'Cobalt',
    label: 'Modern',
    logoCol: '#2563eb',
    nav: '#1e3a8a',
    strip: '#0f2560',
    accent: '#60a5fa',
    subtitle: '#4a6aaa',
    tagline: 'Electric cobalt — tech-forward, modern, and bold.',
  },
  {
    name: 'Ember',
    label: 'Warm',
    logoCol: '#c2410c',
    nav: '#1c0a00',
    strip: '#0f0500',
    accent: '#fb923c',
    subtitle: '#8a4820',
    tagline: 'Warm ember on dark — traditional, intense, and unforgettable.',
  },
  {
    name: 'Slate',
    label: 'Minimal',
    logoCol: '#475569',
    nav: '#1e293b',
    strip: '#0f172a',
    accent: '#94a3b8',
    subtitle: '#475569',
    tagline: 'Cool industrial slate — clean, minimal, and quietly confident.',
  },
];

const GENRES = ['National News', 'State News', 'Local News', 'Sports', 'Technology'];

const MOCK_ARTICLES = [
  { title: 'City Council Approves New Downtown Development Plan', co: 'The Norman Transcript', genre: 'Local News', time: '2h ago' },
  { title: 'State Legislature Passes Education Funding Reform', co: 'Frontier Intelligence', genre: 'State News', time: '4h ago' },
  { title: 'Tech Giants Report Record Earnings Amid Market Uncertainty', co: 'Volt News Network', genre: 'Technology', time: '6h ago' },
  { title: 'Regional Team Advances to Championship After Overtime Win', co: 'Summit Dispatch', genre: 'Sports', time: '8h ago' },
];

function SwatchBlock({ label, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', padding: '12px 14px', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', flex: '1', minWidth: '140px' }}>
      <div style={{ width: '34px', height: '34px', borderRadius: '6px', background: color, flexShrink: 0, border: '1px solid rgba(0,0,0,0.12)' }} />
      <div>
        <div style={{ fontSize: '10px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: '700' }}>{label}</div>
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#333', fontFamily: 'monospace', marginTop: '2px' }}>{color}</div>
      </div>
    </div>
  );
}

function MiniSchemeCard({ s, active, onClick }) {
  return (
    <div
      onClick={onClick}
      title={s.name}
      style={{ borderRadius: '8px', overflow: 'hidden', cursor: 'pointer', border: active ? '2px solid #333' : '2px solid transparent', boxShadow: active ? '0 0 0 2px #33333340' : '0 2px 6px rgba(0,0,0,0.1)', transition: 'border 0.15s, box-shadow 0.15s' }}
    >
      <div style={{ height: '28px', background: s.logoCol }} />
      <div style={{ height: '30px', background: s.nav, display: 'flex', alignItems: 'center', paddingLeft: '8px', gap: '6px' }}>
        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: s.accent, flexShrink: 0 }} />
        <div style={{ fontSize: '10px', color: '#fff', fontWeight: '700', letterSpacing: '0.03em', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{s.name}</div>
      </div>
      <div style={{ height: '8px', background: s.strip }} />
      <div style={{ background: s.nav, padding: '4px 8px' }}>
        <div style={{ fontSize: '9px', color: '#aaa', fontStyle: 'italic' }}>{s.label}</div>
      </div>
    </div>
  );
}

function NavbarRoute() {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const s = SCHEMES[idx];

  const prev = () => setIdx(i => (i - 1 + SCHEMES.length) % SCHEMES.length);
  const next = () => setIdx(i => (i + 1) % SCHEMES.length);

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', fontFamily: 'Arial, sans-serif' }}>

      {/* ── Navbar Preview ── */}
      <nav style={{
        display: 'grid',
        gridTemplateColumns: '220px 1fr 300px',
        gridTemplateRows: '110px 36px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.55)',
        transition: 'all 0.35s ease',
      }}>
        {/* Logo column */}
        <div
          onClick={() => navigate('/')}
          style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: s.logoCol, cursor: 'pointer', transition: 'background 0.35s ease' }}
        >
          <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain' }} />
        </div>

        {/* Center row 1 */}
        <div style={{ gridColumn: 2, gridRow: 1, background: s.nav, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', transition: 'background 0.35s ease' }}>
          <div style={{ color: s.subtitle, fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'color 0.35s ease' }}>Independent Local Publishing</div>
          <div style={{ color: '#ffffff', fontSize: '30px', fontWeight: '300', letterSpacing: '0.06em', lineHeight: 1 }}>PubSilo</div>
        </div>

        {/* Right column */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', background: s.nav, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', transition: 'background 0.35s ease' }}>
          <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '13px', fontWeight: '700', padding: '8px 15px', border: '1px solid #3a3a3a', borderRadius: '5px' }}>Login</Link>
          <button style={{ background: s.accent, color: '#fff', border: 'none', fontSize: '13px', fontWeight: '700', padding: '9px 20px', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.35s ease' }}>Sign Up Free</button>
        </div>

        {/* Row 2 genre strip */}
        <div style={{ gridColumn: 2, gridRow: 2, background: s.strip, display: 'flex', alignItems: 'center', paddingLeft: '20px', gap: '24px', overflow: 'hidden', transition: 'background 0.35s ease' }}>
          {GENRES.map(g => (
            <span key={g} style={{ color: '#999', fontSize: '12px', fontWeight: '700', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{g}</span>
          ))}
        </div>
      </nav>

      {/* ── Control Bar ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '20px 32px', display: 'flex', alignItems: 'center', gap: '20px' }}>
        <button onClick={prev} style={{ background: '#1e293b', color: '#fff', border: 'none', borderRadius: '7px', padding: '10px 22px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>← Prev</button>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <div>
            <span style={{ fontSize: '18px', fontWeight: '800', color: '#111' }}>{s.name}</span>
            <span style={{ fontSize: '13px', fontWeight: '400', color: '#999', marginLeft: '8px' }}>{s.label} · {idx + 1} / {SCHEMES.length}</span>
          </div>
          {/* Dot nav */}
          <div style={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
            {SCHEMES.map((sc, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                title={sc.name}
                style={{ width: i === idx ? '22px' : '10px', height: '10px', borderRadius: i === idx ? '5px' : '50%', border: 'none', background: i === idx ? s.logoCol : '#cbd5e1', cursor: 'pointer', padding: 0, transition: 'all 0.25s ease' }}
              />
            ))}
          </div>
        </div>

        <button onClick={next} style={{ background: '#1e293b', color: '#fff', border: 'none', borderRadius: '7px', padding: '10px 22px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>Next →</button>
      </div>

      {/* ── Main Content ── */}
      <div style={{ maxWidth: '960px', margin: '32px auto', padding: '0 24px 64px' }}>

        {/* Tagline */}
        <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '28px', fontStyle: 'italic', lineHeight: '1.6' }}>{s.tagline}</p>

        {/* Color swatches */}
        <div style={{ fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '10px' }}>Color Palette</div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '36px', flexWrap: 'wrap' }}>
          <SwatchBlock label="Logo Column" color={s.logoCol} />
          <SwatchBlock label="Nav Background" color={s.nav} />
          <SwatchBlock label="Strip / Row 2" color={s.strip} />
          <SwatchBlock label="Accent" color={s.accent} />
        </div>

        {/* Mock article feed */}
        <div style={{ fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '10px' }}>Feed Preview</div>
        <div style={{ background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: '36px' }}>
          {MOCK_ARTICLES.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: i < MOCK_ARTICLES.length - 1 ? '1px solid #f1f5f9' : 'none', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'inline-block', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em', color: s.accent, marginBottom: '4px', transition: 'color 0.35s ease' }}>{a.genre}</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', lineHeight: '1.35' }}>{a.title}</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '3px' }}>{a.co} · {a.time}</div>
              </div>
              <button style={{ background: s.logoCol, color: '#fff', border: 'none', borderRadius: '5px', padding: '7px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.35s ease' }}>Read</button>
            </div>
          ))}
        </div>

        {/* All schemes gallery */}
        <div style={{ fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '12px' }}>All Schemes — Click to Select</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '12px', marginBottom: '40px' }}>
          {SCHEMES.map((sc, i) => (
            <MiniSchemeCard key={i} s={sc} active={i === idx} onClick={() => setIdx(i)} />
          ))}
        </div>

        {/* Back link */}
        <div style={{ textAlign: 'center' }}>
          <Link to="/" style={{ color: '#475569', textDecoration: 'none', fontSize: '13px', fontWeight: '700', padding: '10px 24px', border: '1px solid #cbd5e1', borderRadius: '7px' }}>← Back to PubSilo</Link>
        </div>

      </div>
    </div>
  );
}

export default NavbarRoute;
